<template>
  <section class="marketing-materials" dir="rtl">
    <mainLoader v-if="loading" />

    <div v-else>
      <errorAlert
        v-if="hasError"
        :alertMessage="message"
        :errorNumber="errorNumber"
      />

      <div v-if="property" class="property-summary mb-4">
        <div class="summary-item">
          <span>رقم العقار والنوع</span>
          <strong>#{{ property.id }} - {{ property.PropertyType?.name || property.title || 'عقار' }}</strong>
        </div>
        <div class="summary-item">
          <span>الموقع / المدينة / الحي</span>
          <strong>{{ propertyLocation }}</strong>
        </div>
        <div class="summary-item">
          <span>رقم الترخيص الإعلاني</span>
          <strong>{{ adLicenseNumber || 'غير محدد' }}</strong>
        </div>
        <div class="summary-item">
          <span>رقم الحارس / التنسيق</span>
          <strong>{{ coordinationMobile || 'غير محدد' }}</strong>
        </div>
        <div class="summary-item">
          <span>مساحة العقار</span>
          <strong>{{ property.area ? `${property.area} م²` : 'غير محدد' }}</strong>
        </div>
        <div class="summary-item">
          <span>مدير المشروع / التعميد</span>
          <strong>{{ propertyAdditional?.ProjectManager?.name || 'غير محدد' }}</strong>
        </div>
      </div>

      <div class="section-title mb-3">
        <i class="bi bi-plus-circle-fill"></i>
        إنشاء طلب تسويقي جديد للعقار
      </div>

      <div class="services-grid mb-4">
        <article class="service-card service-card--banner">
          <div class="service-card__head">
            <span class="service-icon service-icon--green"><i class="bi bi-key-fill"></i></span>
            <span class="service-badge service-badge--green">إرسال للتعميد</span>
          </div>
          <h5>طلب تركيب / تحديث لوحة</h5>
          <p>تحديد خامة اللوحة ومقاسها المطلوب، ثم إرسال الطلب للتعميد والإسناد إلى الموظف المسؤول.</p>
          <button
            v-if="canCreate"
            type="button"
            class="service-link service-link--green"
            :disabled="hasOpenRequest"
            @click="openBannerRequest"
          >
            {{ currentBanner ? 'تقديم طلب تحديث لوحة' : 'تقديم طلب لوحة جديد' }}
            <i class="bi bi-arrow-left"></i>
          </button>
          <span v-else class="text-muted small">عرض فقط</span>
          <small v-if="hasOpenRequest" class="d-block text-danger fw-bold mt-2">
            يوجد طلب لوحة قائم على العقار بالفعل
          </small>
        </article>

        <article class="service-card">
          <div class="service-card__head">
            <span class="service-icon service-icon--blue"><i class="bi bi-camera-fill"></i></span>
            <span class="service-badge service-badge--blue">تصوير احترافي</span>
          </div>
          <h5>طلب تصوير العقار</h5>
          <p>طلب مصور محترف، جولة افتراضية 360، تصوير طيران درون أو فيديوهات قصيرة للمنصات.</p>
          <button v-if="canCreate" type="button" class="service-link service-link--blue" :disabled="hasOpenMarketingRequest('photography')" @click="openMarketingForm('photography')">
            تقديم طلب تصوير <i class="bi bi-arrow-left"></i>
          </button>
          <span v-else class="text-muted small">عرض فقط</span>
          <small v-if="hasOpenMarketingRequest('photography')" class="d-block text-danger fw-bold mt-2">
            يوجد طلب تصوير قائم على العقار بالفعل
          </small>
        </article>

        <article class="service-card">
          <div class="service-card__head">
            <span class="service-icon service-icon--purple"><i class="bi bi-share-fill"></i></span>
            <span class="service-badge service-badge--purple">حملات إعلانية</span>
          </div>
          <h5>طلب نشر للسوشيال ميديا</h5>
          <p>تصميم وتجهيز منشورات على منصات التواصل مع استهدافات جاهزة.</p>
          <button v-if="canCreate" type="button" class="service-link service-link--purple" :disabled="hasOpenMarketingRequest('social')" @click="openMarketingForm('social')">
            طلب نشر إعلاني <i class="bi bi-arrow-left"></i>
          </button>
          <span v-else class="text-muted small">عرض فقط</span>
          <small v-if="hasOpenMarketingRequest('social')" class="d-block text-danger fw-bold mt-2">
            يوجد طلب نشر قائم على العقار بالفعل
          </small>
        </article>
      </div>

      <div v-if="showMarketingForm && !readOnly" class="marketing-modal-backdrop" @click.self="closeMarketingForm">
        <section class="marketing-modal" role="dialog" aria-modal="true" aria-labelledby="marketingFormTitle">
          <header class="marketing-modal__head">
            <div>
              <h5 id="marketingFormTitle" class="mb-0">{{ marketingService === 'photography' ? 'طلب تصوير احترافي للعقار' : 'طلب نشر على وسائل التواصل الاجتماعي' }}</h5>
              <small class="text-muted">العقار #{{ propertyId }} · {{ property?.PropertyType?.name || property?.title || 'عقار' }}</small>
            </div>
            <button type="button" class="btn-close" aria-label="إغلاق" @click="closeMarketingForm"></button>
          </header>
          <form @submit.prevent="saveMarketingRequest">
            <div v-if="marketingService === 'social'" class="mb-3">
              <label class="form-label">منصات النشر المطلوبة <span class="req">*</span></label>
              <div class="d-flex flex-wrap gap-2">
                <label v-for="platform in socialPlatforms" :key="platform.value" class="marketing-platform">
                  <input v-model="marketingForm.platforms" type="checkbox" :value="platform.value" /> {{ platform.label }}
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">{{ marketingService === 'photography' ? 'نوع التصوير المطلوب' : 'نوع التصميم / الحملة التسويقية' }} <span class="req">*</span></label>
              <select v-model="marketingForm.optionId" class="form-select" required>
                <option value="">اختر النوع</option>
                <option v-for="option in marketingService === 'photography' ? photographyOptions : socialOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
              </select>
            </div>
            <div v-if="marketingService === 'photography'" class="mb-3">
              <label class="form-label">تاريخ وموعد التصوير المقترح</label>
              <input v-model="marketingForm.shootingDate" type="date" class="form-control" />
            </div>
            <div class="mb-3">
              <label class="form-label">{{ marketingService === 'photography' ? 'توجيهات وملاحظات للمصور' : 'ملاحظات النص الإعلاني / الكابشن' }}</label>
              <textarea v-model="marketingForm.notes" class="form-control" rows="3" maxlength="5000" :placeholder="marketingService === 'photography' ? 'اذكر اللقطات والأماكن المطلوبة...' : 'اكتب تفاصيل النص الإعلاني...'" ></textarea>
            </div>
            <footer class="d-flex gap-2 justify-content-end">
              <button type="button" class="btn btn-light" :disabled="actionLoading" @click="closeMarketingForm">إلغاء</button>
              <button type="submit" class="btn" :class="marketingService === 'photography' ? 'btn-primary' : 'btn-purple'" :disabled="actionLoading">
                {{ actionLoading ? 'جارٍ الإرسال...' : 'إرسال الطلب للتعميد' }}
              </button>
            </footer>
          </form>
        </section>
      </div>

      <div v-if="showRequestForm && !readOnly" ref="requestPanel" class="action-panel mb-4">
        <div class="action-panel__head">
          <div>
            <h5 class="mb-1">{{ requestFormTitle }}</h5>
            <small class="text-muted">سيتم تعيين غرض اللوحة تلقائيًا من العقار وإنشاء الطلب بحالة بانتظار التعميد.</small>
          </div>
          <button type="button" class="btn-close" aria-label="إغلاق" @click="closeRequestForm"></button>
        </div>
        <div class="row">
          <div v-if="requestForm.requestType !== 'removal'" class="col-12 col-md-6 mb-3">
            <label class="form-label">
              نوع خامة اللوحة <span class="req">*</span>
            </label>
            <select
              v-model="requestForm.material"
              class="form-control"
            >
              <option value="">اختر نوع الخامة</option>
              <option v-for="option in materialOptions" :key="option.id" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div v-if="requestForm.requestType !== 'removal'" class="col-12 col-md-6 mb-3">
            <label class="form-label">
              مقاس اللوحة المطلوب <span class="req">*</span>
            </label>
            <select v-model="requestForm.size" class="form-select">
              <option value="">اختر المقاس</option>
              <option v-if="requestForm.size && !sizeOptions.some(option => option.name === requestForm.size)" :value="requestForm.size">{{ requestForm.size }}</option>
              <option v-for="option in sizeOptions" :key="option.id" :value="option.name">{{ option.name }}</option>
            </select>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">ملاحظات الطلب <span class="req">*</span></label>
            <textarea v-model="requestForm.adminNote" class="form-control" rows="2" placeholder="اكتب ملاحظات الطلب"></textarea>
          </div>
        </div>
        <div class="text-end">
          <button type="button" class="btn btn-light ms-2" :disabled="actionLoading" @click="closeRequestForm">إلغاء</button>
          <button type="button" class="btn btn-success" :disabled="actionLoading" @click="createBannerRequest">
            <span v-if="actionLoading" class="spinner-border spinner-border-sm ms-1"></span>
            {{ editingRequestId ? 'حفظ التعديلات' : 'إرسال للتعميد' }}
          </button>
        </div>
      </div>

      <div v-if="currentBanner" class="current-banner mb-4">
        <div class="current-banner__head">
          <div class="d-flex align-items-center gap-3">
            <span class="service-icon service-icon--solid"><i class="bi bi-key-fill"></i></span>
            <div>
              <div class="d-flex align-items-center flex-wrap gap-2">
                <h5 class="mb-0">حالة اللوحة الحالية على العقار</h5>
                <span :class="['status-pill', bannerStatusClass(currentBanner.status)]">
                  {{ bannerStatusChoices[currentBanner.status] || currentBanner.status }}
                </span>
              </div>
              <small class="text-muted">معلومات اللوحة الحالية المثبتة</small>
            </div>
          </div>
          <div v-if="canCreate" class="current-banner__actions">
            <button
              type="button"
              class="btn btn-outline-warning btn-sm"
              :disabled="hasOpenRequest || actionLoading"
              @click="openBannerRequest"
            >
              <i class="bi bi-arrow-repeat"></i> طلب تحديث اللوحة
            </button>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              :disabled="hasOpenRequest || actionLoading"
              @click="requestRemoval"
            >
              <i class="bi bi-trash"></i> طلب إزالة اللوحة
            </button>
          </div>
        </div>

        <div class="banner-details">
          <div class="detail-item">
            <span>رقم اللوحة</span>
            <strong>{{ currentBanner.bannerNumber }}</strong>
          </div>
          <div class="detail-item">
            <span>غرض اللوحة</span>
            <strong>{{ purposeChoices[currentBanner.purpose] || currentBanner.purpose }}</strong>
          </div>
          <div class="detail-item">
            <span>نوع الخامة</span>
            <strong>{{ materialLabel(currentBanner.material) }}</strong>
          </div>
          <div class="detail-item">
            <span>مقاس اللوحة</span>
            <strong>{{ currentBanner.size || currentBanner.PropertyBannerSize?.name || 'غير محدد' }}</strong>
          </div>
          <div class="detail-item">
            <span>سعر مقاس اللوحة</span>
            <strong>{{ formatBannerPrice(property.bannerSizePrice) }}</strong>
          </div>
          <div class="detail-item">
            <span>حالة سداد طلب اللوحة</span>
            <strong :class="property.bannerRequestPaid ? 'text-success' : 'text-danger'">
              {{ property.bannerRequestPaid ? 'تم السداد' : 'غير مسدد' }}
            </strong>
          </div>
          <div class="detail-item">
            <span>جوال طلب اللوحة</span>
            <strong>{{ property.bannerMobile || 'غير محدد' }}</strong>
          </div>
          <div class="detail-item">
            <span>معرّف مقاس اللوحة</span>
            <strong>{{ property.bannerSizeId || currentBanner.bannerSizeId || 'غير محدد' }}</strong>
          </div>
          <div class="detail-item">
            <span>تاريخ التركيب</span>
            <strong>{{ formatDate(currentBanner.installedAt) }}</strong>
          </div>
          <div class="detail-item">
            <span>الموظف المسؤول</span>
            <strong>{{ currentBanner.ResponsibleAdmin?.name || 'غير محدد' }}</strong>
          </div>
          <div class="detail-item detail-item--image">
            <span>صورة اللوحة</span>
            <a v-if="currentBanner.image" :href="MEDIA_URL + currentBanner.image" target="_blank">
              <img :src="MEDIA_URL + currentBanner.image" alt="صورة اللوحة" />
              <strong>مرفقة بالصور</strong>
            </a>
            <strong v-else>غير مرفقة</strong>
          </div>
        </div>
      </div>

      <div v-else class="empty-banner mb-4">
        <i class="bi bi-signpost-2"></i>
        لا توجد لوحة مركبة حاليًا على هذا العقار
      </div>

      <div v-if="bannerRequests.length" class="requests-section mb-4">
        <div class="section-title mb-3">
          <i class="bi bi-clock-history"></i>
          1. طلبات اللوحات
          <span class="count-badge">{{ bannerRequests.length }}</span>
        </div>

        <div class="table-responsive">
          <table class="table align-middle requests-table">
            <thead>
              <tr>
                <th>#</th>
                <th>نوع الطلب</th>
                <th>الخامة والمقاس</th>
                <th>ملاحظات الطلب</th>
                <th>الحالة</th>
                <th>الموظفون</th>
                <th>صورة التنفيذ</th>
                <th>التاريخ</th>
                <th v-if="canUpdate || canDelete || hasWorkflowAccess || hasPrintableRequests">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="request in bannerRequests" :key="request.id">
                <td><strong>#{{ request.id }}</strong></td>
                <td>
                  {{ requestTypeChoices[request.requestType] || request.requestType }}
                  <small class="d-block text-muted">{{ request.requestSource === 'app' ? 'التطبيق' : 'الداشبورد' }}</small>
                </td>
                <td>
                  <strong>{{ materialLabel(request.material) }}</strong>
                  <small class="d-block text-muted">{{ request.size || request.PropertyBannerSize?.name || 'غير محدد' }}</small>
                </td>
                <td>
                  <span v-if="request.adminNote">{{ request.adminNote }}</span>
                  <span v-else class="text-muted">لا توجد ملاحظات</span>
                  <small v-if="request.approvalNote" class="d-block mt-1">
                    <strong>ملاحظات التعميد:</strong> {{ request.approvalNote }}
                  </small>
                </td>
                <td>
                  <span :class="['status-pill', requestStatusClass(request.status)]">
                    {{ requestStatusChoices[request.status] || request.status }}
                  </span>
                </td>
                <td>
                  <small class="d-block"><strong>طالب اللوحة:</strong> {{ request.RequestedBy?.name || (request.requestSource === 'app' ? 'طلب من التطبيق' : 'غير محدد') }}</small>
                  <small class="d-block"><strong>مسؤول التعميد:</strong> {{ request.ApprovalManager?.name || 'غير محدد' }}</small>
                  <small class="d-block"><strong>مسؤول التنفيذ:</strong> {{ request.AssignedAdmin?.name || 'لم يُسند' }}</small>
                </td>
                <td class="detail-item--image">
                  <a v-if="request.executionImage" :href="MEDIA_URL + request.executionImage" target="_blank">
                    <i class="bi bi-image"></i> فتح الصورة
                  </a>
                  <span v-else class="text-muted">غير مرفقة</span>
                </td>
                <td>{{ formatDate(request.createdAt) }}</td>
                <td v-if="canUpdate || canDelete || hasWorkflowAccess || executionSheetAvailable(request)" class="request-actions">
                  <button
                    v-if="executionSheetAvailable(request)"
                    type="button"
                    class="execution-sheet-btn"
                    :disabled="executionSheetLoadingId === request.id"
                    @click="openExecutionSheet(request)"
                  >
                    <span v-if="executionSheetLoadingId === request.id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-file-earmark-pdf-fill"></i>
                    نموذج التنفيذ PDF
                  </button>

                  <button
                    v-if="request.status === 'pending_approval' && canApproveRequest(request)"
                    type="button"
                    class="btn btn-success btn-sm"
                    @click="openApproval(request)"
                  >
                    <i class="bi bi-check2-circle"></i> تعميد
                  </button>

                  <div v-if="request.status === 'approved' && canApproveRequest(request)" class="assignment-control">
                    <select v-model="assignmentMap[request.id]" class="form-select form-select-sm">
                      <option :value="null">اختر الموظف</option>
                      <option v-for="admin in adminsList" :key="admin.id" :value="admin.id">{{ admin.name }}</option>
                    </select>
                    <button type="button" class="btn btn-primary btn-sm" @click="assignRequest(request)">إسناد</button>
                  </div>

                  <button
                    v-if="request.status === 'pending_approval' && canUpdate"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="openEditRequest(request)"
                  >
                    <i class="bi bi-pencil"></i> تعديل
                  </button>

                  <button
                    v-if="['assigned', 'in_progress'].includes(request.status) && canApproveRequest(request)"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="reassignRequest(request)"
                  >
                    <i class="bi bi-person-gear"></i> تعديل الإسناد
                  </button>

                  <button
                    v-if="request.status === 'assigned' && canExecuteRequest(request)"
                    type="button"
                    class="btn btn-primary btn-sm"
                    @click="startRequest(request)"
                  >
                    <i class="bi bi-play-fill"></i> بدء التنفيذ
                  </button>

                  <button
                    v-if="canDelete"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="deleteRequest(request)"
                  >
                    <i class="bi bi-trash"></i> حذف
                  </button>

                  <button
                    v-if="request.status === 'in_progress' && canExecuteRequest(request)"
                    type="button"
                    class="btn btn-dark btn-sm"
                    @click="openCompletion(request)"
                  >
                    <i class="bi bi-check-lg"></i> إكمال الطلب
                  </button>

                  <button
                    v-if="canRejectOrCancel(request)"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="rejectOrCancelRequest(request)"
                  >
                    {{ ['pending_approval', 'approved'].includes(request.status) ? 'رفض' : 'إلغاء' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-for="section in marketingSections" :key="section.service" class="requests-section mb-4">
        <div class="section-title mb-3"><i class="bi bi-megaphone"></i> {{ section.number }}. {{ section.title }} <span class="count-badge">{{ section.rows.length }}</span></div>
        <div class="table-responsive">
          <table class="table align-middle requests-table">
            <thead><tr><th>#</th><th>الخدمة والنوع</th><th>التفاصيل</th><th>الحالة</th><th>الموظفون</th><th>التاريخ</th><th>الإجراءات</th></tr></thead>
            <tbody>
              <tr v-for="request in section.rows" :key="`${request.service}-${request.id}`">
                <td><strong>#{{ request.id }}</strong></td>
                <td><strong>{{ request.service === 'photography' ? 'تصوير العقار' : 'نشر للسوشيال ميديا' }}</strong><small class="d-block text-muted">{{ request.optionName }}</small></td>
                <td>
                  <small v-if="request.shootingDate" class="d-block">التصوير: {{ formatDate(request.shootingDate) }}</small>
                  <small v-if="request.platforms?.length" class="d-block">المنصات: {{ request.platforms.map(platformLabel).join('، ') }}</small>
                  <small class="d-block">{{ request.notes || 'لا توجد ملاحظات' }}</small>
                </td>
                <td><span :class="['status-pill', requestStatusClass(request.status)]">{{ requestStatusChoices[request.status] || request.status }}</span><small v-if="request.rejectionReason" class="d-block">{{ request.rejectionReason }}</small></td>
                <td><small class="d-block">مسؤول التعميد: {{ request.ApprovalManager?.name || 'غير محدد' }}</small><small class="d-block">مسؤول التنفيذ: {{ request.AssignedAdmin?.name || 'لم يُسند' }}</small></td>
                <td>{{ formatDate(request.createdAt) }}</td>
                <td class="request-actions">
                  <button v-if="request.status === 'pending_approval' && canApproveRequest(request)" type="button" class="btn btn-success btn-sm" @click="updateMarketingRequest(request, {status: 'approved'}, 'تم تعميد الطلب')">تعميد</button>
                  <div v-if="request.status === 'approved' && canApproveRequest(request)" class="assignment-control">
                    <select v-model="marketingAssignmentMap[request.id]" class="form-select form-select-sm"><option :value="null">اختر الموظف</option><option v-for="admin in adminsList" :key="admin.id" :value="admin.id">{{ admin.name }}</option></select>
                    <button type="button" class="btn btn-primary btn-sm" @click="assignMarketingRequest(request)">إسناد</button>
                  </div>
                  <button v-if="request.status === 'assigned' && canExecuteRequest(request)" type="button" class="btn btn-primary btn-sm" @click="updateMarketingRequest(request, {status: 'in_progress'}, 'بدأ التنفيذ')">بدء التنفيذ</button>
                  <button v-if="request.status === 'in_progress' && canExecuteRequest(request)" type="button" class="btn btn-dark btn-sm" @click="updateMarketingRequest(request, {status: 'completed'}, 'اكتمل الطلب')">إكمال</button>
                  <button v-if="request.status === 'pending_approval' && canUpdate" type="button" class="btn btn-outline-primary btn-sm" @click="openMarketingForm(request.service, request)">تعديل</button>
                  <button v-if="canRejectOrCancel(request)" type="button" class="btn btn-outline-warning btn-sm" @click="rejectMarketingRequest(request)">{{ ['pending_approval', 'approved'].includes(request.status) ? 'رفض' : 'إلغاء' }}</button>
                  <button v-if="canDelete" type="button" class="btn btn-outline-danger btn-sm" @click="deleteMarketingRequest(request)">حذف</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="approvalRequest && canApproveRequest(approvalRequest)" ref="approvalPanel" class="action-panel mt-4">
        <div class="action-panel__head">
          <div>
            <h5 class="mb-1">تعميد الطلب #{{ approvalRequest.id }}</h5>
            <small class="text-muted">غرض اللوحة محدد تلقائيًا من العقار.</small>
          </div>
          <button type="button" class="btn-close" @click="approvalRequest = null"></button>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <div class="alert alert-light border mb-0">
              <strong class="d-block mb-1">نوع خامة اللوحة</strong>
              <span>{{ materialLabel(approvalRequest.material) }}</span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="alert alert-light border mb-0">
              <strong class="d-block mb-1">مقاس اللوحة المطلوب</strong>
              <span>{{ approvalRequest.size || approvalRequest.PropertyBannerSize?.name || 'غير محدد' }}</span>
            </div>
          </div>
        </div>
        <div class="alert alert-light border mb-3">
          <strong class="d-block mb-1">ملاحظات الطلب</strong>
          <span>{{ approvalRequest.adminNote || 'لا توجد ملاحظات' }}</span>
        </div>
        <div v-if="requiresApprovalNote(approvalRequest)" class="mb-3">
          <label class="form-label">
            ملاحظات التعميد <span class="req">*</span>
          </label>
          <textarea
            v-model="approvalNote"
            class="form-control"
            rows="3"
            placeholder="اكتب ملاحظات تعميد تحديث أو إزالة اللوحة"
          ></textarea>
        </div>
        <div class="text-end">
          <button type="button" class="btn btn-light ms-2" @click="approvalRequest = null">إلغاء</button>
          <button type="button" class="btn btn-success" :disabled="actionLoading" @click="approveRequest">
            <span v-if="actionLoading" class="spinner-border spinner-border-sm ms-1"></span>
            اعتماد الطلب
          </button>
        </div>
      </div>

      <div v-if="completionRequest && !readOnly && canExecuteRequest(completionRequest)" ref="completionPanel" class="action-panel mt-4">
        <div class="action-panel__head">
          <div>
            <h5 class="mb-1">إكمال الطلب #{{ completionRequest.id }}</h5>
            <small class="text-muted">
              {{ completionRequest.requestType === 'removal' ? 'أكد تاريخ إزالة اللوحة.' : 'أدخل بيانات التركيب وارفع صورة اللوحة.' }}
            </small>
          </div>
          <button type="button" class="btn-close" @click="closeCompletion"></button>
        </div>

        <div v-if="completionRequest.requestType === 'removal'" class="row">
          <div class="col-12 col-md-6 mb-3">
            <label class="form-label">تاريخ الإزالة</label>
            <input v-model="completionForm.removedAt" type="date" class="form-control" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <label class="form-label">صورة إزالة اللوحة <span class="req">*</span></label>
            <input ref="completionImage" type="file" accept=".png,.jpg,.jpeg,.webp" class="form-control" @change="setCompletionImage" />
          </div>
        </div>
        <div v-else class="row">
          <div class="col-12 col-md-4 mb-3">
            <label class="form-label">رقم اللوحة</label>
            <input v-model="completionForm.bannerNumber" type="text" class="form-control" placeholder="يُولّد تلقائيًا عند تركه فارغًا" />
          </div>
          <div class="col-12 col-md-4 mb-3">
            <label class="form-label">تاريخ التركيب</label>
            <input v-model="completionForm.installedAt" type="date" class="form-control" />
          </div>
          <div class="col-12 col-md-4 mb-3">
            <label class="form-label">
              صورة اللوحة
              <span class="req">*</span>
            </label>
            <input ref="completionImage" type="file" accept=".png,.jpg,.jpeg,.webp" class="form-control" @change="setCompletionImage" />
            <small v-if="completionRequest.requestType === 'update'" class="text-muted">تُحفظ صورة التحديث في سجل الطلب ولا تستبدل الصور التاريخية.</small>
          </div>
        </div>
        <div class="text-end">
          <button type="button" class="btn btn-light ms-2" @click="closeCompletion">إلغاء</button>
          <button type="button" class="btn btn-dark" :disabled="actionLoading" @click="completeRequest">
            <span v-if="actionLoading" class="spinner-border spinner-border-sm ms-1"></span>
            تأكيد الإكمال
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { checkAccess, toast } from '@/helpers/tools';

const EMPTY_REQUEST_FORM = () => ({
  requestType: 'installation',
  material: '',
  size: '',
  adminNote: '',
});
const EMPTY_MARKETING_FORM = () => ({optionId: '', shootingDate: '', platforms: [], notes: ''});

const EMPTY_COMPLETION_FORM = () => ({
  bannerNumber: '',
  installedAt: '',
  removedAt: '',
  image: null,
});

export default {
  name: 'PropertyMarketingMaterials',
  props: {
    propertyId: {
      type: [Number, String],
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      actionLoading: false,
      hasError: false,
      message: '',
      errorNumber: 0,
      property: null,
      currentBanner: null,
      bannerRequests: [],
      marketingRequests: [],
      photographyOptions: [],
      socialOptions: [],
      sizeOptions: [],
      socialPlatforms: [
        {value: 'x', label: 'منصة X (تويتر)'},
        {value: 'instagram', label: 'إنستغرام'},
        {value: 'snapchat', label: 'سناب شات'},
        {value: 'tiktok', label: 'تيك توك'},
      ],
      showMarketingForm: false,
      marketingService: 'photography',
      marketingForm: EMPTY_MARKETING_FORM(),
      editingMarketingId: null,
      marketingAssignmentMap: {},
      adminsList: [],
      workflowPermissions: {
        canApprove: false,
        canExecute: false,
        isSuperUser: false,
        currentAdminId: null,
      },
      requestStatusChoices: {},
      requestTypeChoices: {},
      purposeChoices: {},
      bannerStatusChoices: {},
      openBannerRequestsCount: 0,
      showRequestForm: false,
      requestForm: EMPTY_REQUEST_FORM(),
      approvalRequest: null,
      approvalNote: '',
      completionRequest: null,
      completionForm: EMPTY_COMPLETION_FORM(),
      assignmentMap: {},
      propertyAdditional: null,
      materialChoices: {},
      materialOptions: [],
      editingRequestId: null,
      executionSheetLoadingId: null,
    };
  },
  computed: {
    ...mapGetters(['MEDIA_URL']),
    hasOpenRequest() {
      return this.openBannerRequestsCount > 0;
    },
    marketingSections() {
      return [
        {service: 'photography', number: 2, title: 'طلبات تصوير العقار', rows: this.marketingRequests.filter(row => row.service === 'photography')},
        {service: 'social', number: 3, title: 'طلبات النشر للسوشيال ميديا', rows: this.marketingRequests.filter(row => row.service === 'social')},
      ].filter(section => section.rows.length);
    },
    canCreate() {
      return !this.readOnly && checkAccess('property_banner_requests_create');
    },
    canUpdate() {
      return !this.readOnly && checkAccess('property_banner_requests_update');
    },
    canDelete() {
      return !this.readOnly && checkAccess('property_banner_requests_delete');
    },
    hasWorkflowAccess() {
      return !this.readOnly && (
        this.workflowPermissions.canApprove || this.workflowPermissions.canExecute
      );
    },
    hasPrintableRequests() {
      return this.bannerRequests.some(request => this.executionSheetAvailable(request));
    },
    requestFormTitle() {
      if(this.editingRequestId) return `تعديل طلب اللوحة #${this.editingRequestId}`;
      return this.requestForm.requestType === 'update' ? 'طلب تحديث اللوحة الحالية' : 'طلب تركيب لوحة جديدة';
    },
    propertyLocation() {
      if(!this.property) return 'غير محدد';
      const parts = [
        this.property.Region?.name,
        this.property.City?.name,
        this.property.Neighborhood?.name,
      ].filter(Boolean);
      return parts.length ? parts.join(' / ') : 'غير محدد';
    },
    adLicenseNumber() {
      return this.property?.AdValidator?.advertisement?.adLicenseNumber ||
        this.property?.PropertyOwner?.adLicenseNumber || null;
    },
    coordinationMobile() {
      const owner = this.property?.PropertyOwner;
      return this.propertyAdditional?.guardNumber || this.property?.bannerMobile || owner?.agentMobile || owner?.ownerMobile || owner?.User?.mobile || null;
    },
  },
  methods: {
    setError(err) {
      [this.message, this.errorNumber] = this.$globalError(err, true);
      this.hasError = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    setLocalError(message) {
      this.message = message;
      this.errorNumber = 0;
      this.hasError = true;
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    },
    formatBannerPrice(value) {
      if(value === null || value === undefined || value === '') return 'غير محدد';
      const price = Number(value);
      if(Number.isNaN(price)) return value;
      return `${new Intl.NumberFormat('ar-SA', {maximumFractionDigits: 2}).format(price)} ر.س`;
    },
    materialLabel(value) {
      if(!value) return 'غير محدد';
      return this.materialChoices[value] || value;
    },
    executionSheetAvailable(request) {
      return ['approved', 'assigned', 'in_progress', 'completed'].includes(request?.status);
    },
    async openExecutionSheet(request) {
      if(!this.executionSheetAvailable(request) || this.executionSheetLoadingId) return;
      const previewWindow = window.open('', '_blank');
      this.executionSheetLoadingId = request.id;
      this.hasError = false;
      try {
        const { data } = await this.$http.get(
          `/api/admin/propertyBannerRequests/executionSheet/${request.id}`,
          { responseType: 'blob' }
        );
        const pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        if(previewWindow) {
          previewWindow.location.href = pdfUrl;
        } else {
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = `property-banner-request-${request.id}.pdf`;
          link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      } catch(err) {
        if(previewWindow) previewWindow.close();
        if(err.response?.data instanceof Blob) {
          try {
            err.response.data = JSON.parse(await err.response.data.text());
          } catch(_) {
          }
        }
        this.setError(err);
      } finally {
        this.executionSheetLoadingId = null;
      }
    },
    loadMaterials(showLoader=true) {
      if(showLoader) this.loading = true;
      this.hasError = false;
      return this.$http.get(`/api/admin/propertyBannerRequests/property/${this.propertyId}`)
        .then(({data}) => {
          this.property = data.property;
          this.currentBanner = data.currentBanner;
          this.bannerRequests = data.bannerRequests || [];
          this.marketingRequests = data.marketingRequests || [];
          this.photographyOptions = data.photographyOptions || [];
          this.socialOptions = data.socialOptions || [];
          this.sizeOptions = data.sizeOptions || [];
          this.adminsList = data.adminsList || [];
          this.workflowPermissions = data.workflowPermissions || this.workflowPermissions;
          this.requestStatusChoices = data.requestStatusChoices || {};
          this.requestTypeChoices = data.requestTypeChoices || {};
          this.purposeChoices = data.purposeChoices || {};
          this.bannerStatusChoices = data.bannerStatusChoices || {};
          this.openBannerRequestsCount = data.openBannerRequestsCount || 0;
          this.assignmentMap = {};
          this.marketingAssignmentMap = {};
          this.propertyAdditional = data.propertyAdditional || null;
          this.materialChoices = data.materialChoices || {};
          this.materialOptions = data.materialOptions || [];
          this.bannerRequests.forEach(request => {
            this.assignmentMap[request.id] = request.assignedAdminId || null;
          });
          this.marketingRequests.forEach(request => {
            this.marketingAssignmentMap[request.id] = request.assignedAdminId || null;
          });
        })
        .catch(err => this.setError(err))
        .finally(() => {
          this.loading = false;
          this.actionLoading = false;
        });
    },
    scrollTo(refName) {
      this.$nextTick(() => {
        this.$refs[refName]?.scrollIntoView({behavior: 'smooth', block: 'center'});
      });
    },
    platformLabel(value) {
      return this.socialPlatforms.find(platform => platform.value === value)?.label || value;
    },
    hasOpenMarketingRequest(service) {
      return this.marketingRequests.some(request => request.service === service && this.isOpenStatus(request.status));
    },
    openMarketingForm(service, request=null) {
      if((request && !this.canUpdate) || (!request && !this.canCreate)) return;
      if(!request && this.hasOpenMarketingRequest(service)) {
        this.setLocalError(service === 'photography' ? 'يوجد طلب تصوير قائم على العقار بالفعل' : 'يوجد طلب نشر قائم على العقار بالفعل');
        return;
      }
      if(!this.propertyAdditional?.projectManagerId) {
        this.setLocalError('يجب اختيار مدير المشروع المسؤول عن التعميد في البيانات الإضافية للعقار أولاً');
        return;
      }
      this.marketingService = service;
      this.editingMarketingId = request?.id || null;
      this.marketingForm = request ? {
        optionId: request.optionId || '', shootingDate: request.shootingDate || '',
        platforms: [...(request.platforms || [])], notes: request.notes || '',
      } : EMPTY_MARKETING_FORM();
      this.showMarketingForm = true;
    },
    closeMarketingForm() {
      if(this.actionLoading) return;
      this.showMarketingForm = false;
      this.editingMarketingId = null;
      this.marketingForm = EMPTY_MARKETING_FORM();
    },
    async saveMarketingRequest() {
      if(!this.editingMarketingId && this.hasOpenMarketingRequest(this.marketingService))
        return this.setLocalError(this.marketingService === 'photography' ? 'يوجد طلب تصوير قائم على العقار بالفعل' : 'يوجد طلب نشر قائم على العقار بالفعل');
      if(!this.marketingForm.optionId) return this.setLocalError('نوع الطلب مطلوب');
      if(this.marketingService === 'social' && !this.marketingForm.platforms.length)
        return this.setLocalError('يجب اختيار منصة نشر واحدة على الأقل');
      const payload = {
        service: this.marketingService,
        optionId: this.marketingForm.optionId,
        shootingDate: this.marketingService === 'photography' ? this.marketingForm.shootingDate : null,
        platforms: this.marketingService === 'social' ? this.marketingForm.platforms : null,
        notes: this.marketingForm.notes,
      };
      this.actionLoading = true;
      this.hasError = false;
      try {
        if(this.editingMarketingId)
          await this.$http.put(`/api/admin/propertyMarketingRequests/edit/${this.editingMarketingId}`, payload);
        else
          await this.$http.post(`/api/admin/propertyMarketingRequests/property/${this.propertyId}`, payload);
        toast('تم إرسال الطلب للتعميد بنجاح', 'success');
        this.actionLoading = false;
        this.closeMarketingForm();
        await this.loadMaterials(false);
      } catch(err) {this.actionLoading = false; this.setError(err);}
    },
    async updateMarketingRequest(request, payload, message) {
      this.actionLoading = true;
      this.hasError = false;
      try {
        await this.$http.put(`/api/admin/propertyMarketingRequests/update/${request.id}`, payload);
        toast(message, 'success');
        await this.loadMaterials(false);
      } catch(err) {this.actionLoading = false; this.setError(err);}
    },
    assignMarketingRequest(request) {
      const assignedAdminId = this.marketingAssignmentMap[request.id];
      if(!assignedAdminId) return this.setLocalError('يجب اختيار الموظف المسؤول');
      this.updateMarketingRequest(request, {status: 'assigned', assignedAdminId}, 'تم إسناد الطلب');
    },
    async rejectMarketingRequest(request) {
      const rejecting = ['pending_approval', 'approved'].includes(request.status);
      const result = await this.$swal.fire({title: rejecting ? 'رفض الطلب' : 'إلغاء الطلب', input: 'textarea', inputLabel: 'السبب', inputValidator: value => !value?.trim() ? 'السبب مطلوب' : undefined, showCancelButton: true, confirmButtonText: 'تأكيد', cancelButtonText: 'إلغاء'});
      if(result.isConfirmed) await this.updateMarketingRequest(request, {status: rejecting ? 'rejected' : 'cancelled', rejectionReason: result.value.trim()}, 'تم تحديث الطلب');
    },
    async deleteMarketingRequest(request) {
      const result = await this.$swal.fire({title: `حذف الطلب #${request.id}؟`, icon: 'warning', showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء'});
      if(!result.isConfirmed) return;
      this.actionLoading = true;
      try {
        await this.$http.delete(`/api/admin/propertyMarketingRequests/delete/${request.id}`);
        toast('تم حذف الطلب', 'success');
        await this.loadMaterials(false);
      } catch(err) {this.actionLoading = false; this.setError(err);}
    },
    openBannerRequest() {
      if(!this.canCreate || this.hasOpenRequest) return;
      if(!this.validatePropertyWorkflowSetup()) return;
      this.hasError = false;
      this.approvalRequest = null;
      this.completionRequest = null;
      this.editingRequestId = null;
      this.requestForm = {
        requestType: this.currentBanner ? 'update' : 'installation',
        material: this.currentBanner?.material || '',
        size: this.currentBanner?.size || this.currentBanner?.PropertyBannerSize?.name || '',
        adminNote: '',
      };
      this.showRequestForm = true;
      this.scrollTo('requestPanel');
    },
    closeRequestForm() {
      this.showRequestForm = false;
      this.editingRequestId = null;
      this.requestForm = EMPTY_REQUEST_FORM();
    },
    openEditRequest(request) {
      if(!this.canUpdate || request.status !== 'pending_approval') return;
      this.hasError = false;
      this.approvalRequest = null;
      this.completionRequest = null;
      this.editingRequestId = request.id;
      this.requestForm = {
        requestType: request.requestType,
        material: request.material || '',
        size: request.size || request.PropertyBannerSize?.name || '',
        adminNote: request.adminNote || '',
      };
      this.showRequestForm = true;
      this.scrollTo('requestPanel');
    },
    createBannerRequest() {
      const material = this.requestForm.material;
      const size = this.requestForm.size.trim();
      const adminNote = this.requestForm.adminNote.trim();
      if(this.requestForm.requestType !== 'removal' && !material) {
        this.setLocalError('نوع خامة اللوحة مطلوب');
        return;
      }
      if(this.requestForm.requestType !== 'removal' && !size) {
        this.setLocalError('مقاس اللوحة المطلوب مطلوب');
        return;
      }
      if(!adminNote) {
        this.setLocalError('ملاحظات الطلب مطلوبة');
        return;
      }
      this.requestForm.material = material;
      this.requestForm.size = size;
      this.actionLoading = true;
      this.hasError = false;
      this.requestForm.adminNote = adminNote;
      const request = this.editingRequestId
        ? this.$http.put(`/api/admin/propertyBannerRequests/edit/${this.editingRequestId}`, this.requestForm)
        : this.$http.post(`/api/admin/propertyBannerRequests/property/${this.propertyId}`, this.requestForm);
      request
        .then(() => {
          toast(this.editingRequestId ? 'تم تعديل طلب اللوحة بنجاح' : 'تم إرسال طلب اللوحة للتعميد بنجاح', 'success');
          this.closeRequestForm();
          return this.loadMaterials(false);
        })
        .catch(err => {
          this.actionLoading = false;
          this.setError(err);
        });
    },
    requestRemoval() {
      if(!this.canCreate || this.hasOpenRequest) return;
      if(!this.validatePropertyWorkflowSetup()) return;
      this.$swal.fire({
        title: 'طلب إزالة اللوحة',
        input: 'textarea',
        inputLabel: 'ملاحظات طلب الإزالة',
        inputPlaceholder: 'اكتب سبب وملاحظات إزالة اللوحة...',
        inputValidator: value => !value || !value.trim() ? 'ملاحظات الطلب مطلوبة' : undefined,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'إرسال الطلب',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      }).then(result => {
        if(!result.isConfirmed) return;
        this.actionLoading = true;
        this.$http.post(`/api/admin/propertyBannerRequests/property/${this.propertyId}`, {
          requestType: 'removal',
          adminNote: result.value.trim(),
        }).then(() => {
          toast('تم إرسال طلب إزالة اللوحة', 'success');
          return this.loadMaterials(false);
        }).catch(err => {
          this.actionLoading = false;
          this.setError(err);
        });
      });
    },
    openApproval(request) {
      if(!this.canApproveRequest(request)) return;
      this.showRequestForm = false;
      this.completionRequest = null;
      this.approvalRequest = request;
      this.approvalNote = '';
      this.scrollTo('approvalPanel');
    },
    requiresApprovalNote(request) {
      return ['update', 'removal'].includes(request?.requestType);
    },
    approveRequest() {
      if(!this.canApproveRequest(this.approvalRequest)) return;
      const approvalNote = this.approvalNote.trim();
      if(this.requiresApprovalNote(this.approvalRequest) && !approvalNote) {
        this.setLocalError('ملاحظات التعميد مطلوبة لطلبات تحديث وإزالة اللوحة');
        return;
      }
      this.updateRequest(this.approvalRequest.id, {
        status: 'approved',
        ...(approvalNote ? {approvalNote} : {}),
      }, 'تم تعميد الطلب بنجاح').then(success => {
        if(success) {
          this.approvalRequest = null;
          this.approvalNote = '';
        }
      });
    },
    assignRequest(request) {
      if(!this.canApproveRequest(request)) return;
      const assignedAdminId = this.assignmentMap[request.id];
      if(!assignedAdminId){
        this.setLocalError('يجب اختيار الموظف المسؤول عن الطلب');
        return;
      }
      this.updateRequest(request.id, {
        status: 'assigned',
        assignedAdminId,
      }, 'تم إسناد الطلب للموظف');
    },
    canApproveRequest(request) {
      if(this.readOnly || !this.workflowPermissions.canApprove || !request) return false;
      return this.workflowPermissions.isSuperUser ||
        Number(request.approvalManagerId) === Number(this.workflowPermissions.currentAdminId);
    },
    validatePropertyWorkflowSetup() {
      if(!this.propertyAdditional?.guardNumber) {
        this.setLocalError('يجب تسجيل رقم الحارس في البيانات الإضافية للعقار أولاً');
        return false;
      }
      if(!this.propertyAdditional?.projectManagerId) {
        this.setLocalError('يجب اختيار مدير المشروع المسؤول عن التعميد في البيانات الإضافية للعقار أولاً');
        return false;
      }
      return true;
    },
    reassignRequest(request) {
      if(!this.canApproveRequest(request)) return;
      const inputOptions = this.adminsList.reduce((options, admin) => {
        options[admin.id] = admin.name;
        return options;
      }, {});
      this.$swal.fire({
        title: `تعديل إسناد الطلب #${request.id}`,
        input: 'select',
        inputOptions,
        inputValue: request.assignedAdminId || '',
        inputPlaceholder: 'اختر المسؤول التنفيذي',
        inputValidator: value => !value ? 'يجب اختيار المسؤول التنفيذي' : undefined,
        showCancelButton: true,
        confirmButtonText: 'حفظ الإسناد',
        cancelButtonText: 'إلغاء',
      }).then(result => {
        if(!result.isConfirmed) return;
        this.updateRequest(request.id, {
          status: request.status,
          assignedAdminId: result.value,
        }, 'تم تعديل إسناد الطلب');
      });
    },
    startRequest(request) {
      if(!this.canExecuteRequest(request)) return;
      this.updateRequest(request.id, {status: 'in_progress'}, 'تم بدء تنفيذ الطلب');
    },
    canExecuteRequest(request) {
      if(this.readOnly || !this.workflowPermissions.canExecute) return false;
      return this.workflowPermissions.isSuperUser ||
        Number(request.assignedAdminId) === Number(this.workflowPermissions.currentAdminId);
    },
    canRejectOrCancel(request) {
      if(this.readOnly || !this.isOpenStatus(request.status)) return false;
      if(['pending_approval', 'approved'].includes(request.status)) return this.canApproveRequest(request);
      return this.canExecuteRequest(request);
    },
    deleteRequest(request) {
      if(!this.canDelete) return;
      this.$swal.fire({
        title: `حذف طلب اللوحة #${request.id}؟`,
        text: 'سيختفي الطلب من اللوحة مع الاحتفاظ بسجل الحذف والصور السابقة.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم، حذف الطلب',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      }).then(result => {
        if(!result.isConfirmed) return;
        this.actionLoading = true;
        this.$http.delete(`/api/admin/propertyBannerRequests/delete/${request.id}`)
          .then(() => {
            toast('تم حذف طلب اللوحة', 'success');
            return this.loadMaterials(false);
          })
          .catch(err => {
            this.actionLoading = false;
            this.setError(err);
          });
      });
    },
    rejectOrCancelRequest(request) {
      if(!this.canRejectOrCancel(request)) return;
      const isReject = ['pending_approval', 'approved'].includes(request.status);
      this.$swal.fire({
        title: isReject ? 'رفض الطلب' : 'إلغاء الطلب',
        input: 'textarea',
        inputLabel: 'السبب',
        inputPlaceholder: 'اكتب السبب هنا...',
        inputValidator: value => !value ? 'يجب إدخال السبب' : undefined,
        showCancelButton: true,
        confirmButtonText: isReject ? 'رفض' : 'إلغاء الطلب',
        cancelButtonText: 'تراجع',
        confirmButtonColor: '#dc3545',
      }).then(result => {
        if(!result.isConfirmed) return;
        this.updateRequest(request.id, {
          status: isReject ? 'rejected' : 'cancelled',
          rejectionReason: result.value,
        }, isReject ? 'تم رفض الطلب' : 'تم إلغاء الطلب');
      });
    },
    openCompletion(request) {
      if(!this.canExecuteRequest(request)) return;
      this.showRequestForm = false;
      this.approvalRequest = null;
      this.completionRequest = request;
      this.completionForm = {
        ...EMPTY_COMPLETION_FORM(),
        installedAt: this.today(),
        removedAt: this.today(),
      };
      this.scrollTo('completionPanel');
    },
    closeCompletion() {
      this.completionRequest = null;
      this.completionForm = EMPTY_COMPLETION_FORM();
      if(this.$refs.completionImage) this.$refs.completionImage.value = '';
    },
    setCompletionImage(event) {
      this.completionForm.image = event.target.files[0] || null;
    },
    completeRequest() {
      if(!this.completionRequest || !this.canExecuteRequest(this.completionRequest)) return;
      if(!this.completionForm.image){
        this.setLocalError('يجب رفع صورة تنفيذ تركيب أو تحديث أو إزالة اللوحة');
        return;
      }
      const formData = new FormData();
      formData.append('status', 'completed');
      if(this.completionRequest.requestType === 'removal'){
        formData.append('removedAt', this.completionForm.removedAt);
      }else{
        if(this.completionForm.bannerNumber)
          formData.append('bannerNumber', this.completionForm.bannerNumber);
        formData.append('installedAt', this.completionForm.installedAt);
      }
      formData.append('image', this.completionForm.image);
      this.updateRequest(this.completionRequest.id, formData, 'تم إكمال الطلب وتحديث حالة اللوحة').then(success => {
        if(success) this.closeCompletion();
      });
    },
    updateRequest(id, payload, successMessage) {
      this.actionLoading = true;
      this.hasError = false;
      return this.$http.put(`/api/admin/propertyBannerRequests/update/${id}`, payload)
        .then(() => {
          toast(successMessage, 'success');
          return this.loadMaterials(false).then(() => true);
        })
        .catch(err => {
          this.actionLoading = false;
          this.setError(err);
          return false;
        });
    },
    isOpenStatus(status) {
      return ['pending_approval', 'approved', 'assigned', 'in_progress'].includes(status);
    },
    requestStatusClass(status) {
      return {
        pending_approval: 'status-pill--warning',
        approved: 'status-pill--success',
        assigned: 'status-pill--primary',
        in_progress: 'status-pill--info',
        completed: 'status-pill--dark',
        rejected: 'status-pill--danger',
        cancelled: 'status-pill--secondary',
      }[status] || 'status-pill--secondary';
    },
    bannerStatusClass(status) {
      return {
        installed: 'status-pill--success',
        update_pending: 'status-pill--warning',
        removal_pending: 'status-pill--danger',
        removed: 'status-pill--secondary',
      }[status] || 'status-pill--secondary';
    },
    formatDate(value) {
      if(!value) return 'غير محدد';
      const date = new Date(value);
      if(Number.isNaN(date.getTime())) return 'غير محدد';
      return new Intl.DateTimeFormat('ar-EG', {
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(date);
    },
    today() {
      const date = new Date();
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
    },
  },
  created() {
    this.loadMaterials();
  },
};
</script>

<style scoped>
.marketing-materials {
  --pm-text: #152238;
  --pm-muted: #728096;
  --pm-surface: #ffffff;
  --pm-surface-soft: #f8fafc;
  --pm-surface-disabled: #fbfcfe;
  --pm-border: #d9e2ec;
  --pm-border-soft: #e8eef4;
  --pm-accent: #176548;
  --pm-hover: rgba(23, 101, 72, 0.045);
  --pm-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  color: var(--pm-text);
}

.property-summary {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0;
  padding: 16px 8px;
  border: 1px solid var(--pm-border);
  border-radius: 14px;
  background: var(--pm-surface-soft);
}

.summary-item {
  padding: 2px 18px;
  text-align: center;
  border-left: 1px solid var(--pm-border-soft);
}

.summary-item:last-child { border-left: 0; }
.summary-item span,
.detail-item span {
  display: block;
  color: var(--pm-muted);
  font-size: 0.78rem;
  margin-bottom: 3px;
}

.summary-item strong,
.detail-item strong {
  color: var(--pm-text);
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--pm-text);
  font-weight: 700;
}

.section-title i { color: var(--pm-accent); }
.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 12px;
  background: var(--pm-accent);
  color: #fff;
  font-size: 0.75rem;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.service-card {
  display: flex;
  flex-direction: column;
  min-height: 200px;
  padding: 20px;
  border: 1px solid var(--pm-border);
  border-radius: 15px;
  background: var(--pm-surface);
  box-shadow: var(--pm-shadow);
}

.service-card__head,
.current-banner__head,
.action-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.service-card h5 { color: var(--pm-text); font-weight: 700; font-size: 1rem; }
.service-card p {
  min-height: 54px;
  color: var(--pm-muted);
  font-size: 0.82rem;
  line-height: 1.8;
}
.service-card--disabled { background: var(--pm-surface-disabled); }
.service-card--disabled .service-link { opacity: 0.72; }

.service-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 13px;
  font-size: 1.2rem;
}
.service-icon--green { color: #0f7653; background: #d9f7e8; }
.service-icon--blue { color: #2467d8; background: #e1edff; }
.service-icon--purple { color: #9234e9; background: #f2e3ff; }
.service-icon--solid { width: 42px; height: 42px; color: #fff; background: #12b981; }

.service-badge,
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
.service-badge--green { color: #17553e; background: #f0f6f2; border: 1px solid #dce9e1; }
.service-badge--blue { color: #1f5ec0; background: #eef5ff; border: 1px solid #d6e6ff; }
.service-badge--purple { color: #8426c9; background: #fbf2ff; border: 1px solid #f1d9ff; }

.service-link {
  align-self: flex-start;
  margin-top: auto;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 700;
}
.service-link:disabled { cursor: not-allowed; }
.service-link--green { color: #176548; }
.service-link--blue { color: #2365cf; }
.service-link--purple { color: #8b2bd1; }

.action-panel,
.current-banner {
  padding: 20px;
  border: 1px solid var(--pm-border);
  border-radius: 15px;
  background: var(--pm-surface);
  box-shadow: var(--pm-shadow);
}
.action-panel { border-right: 4px solid var(--pm-accent); background: var(--pm-surface-disabled); }
.action-panel__head h5,
.current-banner__head h5 { color: var(--pm-text); font-weight: 700; }
.current-banner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.banner-details {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--pm-border-soft);
}
.detail-item {
  min-height: 72px;
  padding: 12px;
  border: 1px solid var(--pm-border-soft);
  border-radius: 12px;
  background: var(--pm-surface-soft);
  text-align: center;
}
.detail-item--image a {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #168456;
  text-decoration: none;
}
.detail-item--image img {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.empty-banner {
  padding: 22px;
  border: 1px dashed var(--pm-border);
  border-radius: 14px;
  color: var(--pm-muted);
  text-align: center;
  background: var(--pm-surface-soft);
}
.empty-banner i { display: block; margin-bottom: 6px; font-size: 1.5rem; }

.requests-section {
  overflow: hidden;
  border: 1px solid var(--pm-border);
  border-radius: 15px;
  background: var(--pm-surface);
  box-shadow: var(--pm-shadow);
}
.requests-section > .section-title {
  margin-bottom: 0 !important;
  padding: 16px 18px;
  border-bottom: 1px solid var(--pm-border-soft);
}
.requests-section .table-responsive { padding: 0 18px 14px; }
.requests-table {
  --bs-table-color: var(--pm-text);
  --bs-table-bg: transparent;
  --bs-table-border-color: var(--pm-border-soft);
  min-width: 1180px;
   margin-bottom: 0;
}
.requests-table thead th {
  color: var(--pm-muted);
  font-size: 0.78rem;
  border-bottom-width: 1px;
  background: var(--pm-surface-soft);
}
.requests-table td {
  color: var(--pm-text);
  font-size: 0.82rem;
  border-bottom-color: var(--pm-border-soft);
}
.requests-table tbody tr:last-child td { border-bottom: 0; }
.requests-table tbody tr:hover > * { background-color: var(--pm-hover); }
.request-actions { min-width: 245px; }
.request-actions > * { margin: 2px; }
.execution-sheet-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 32px;
  padding: 5px 10px;
  border: 1px solid #d7bd8b;
  border-radius: 7px;
  color: #78591f;
  background: #fffaf0;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.3;
  transition: background-color 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}
.execution-sheet-btn:hover:not(:disabled) {
  color: #614514;
  border-color: #c7a668;
  background: #fff3da;
  transform: translateY(-1px);
}
.execution-sheet-btn:disabled { cursor: wait; opacity: 0.7; }
.assignment-control {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 210px;
}

.status-pill--warning { color: #8a5b00; background: #fff4ce; }
.status-pill--success { color: #087443; background: #dff7ea; }
.status-pill--primary { color: #1559b5; background: #e4efff; }
.status-pill--info { color: #08708b; background: #dff6fb; }
.status-pill--dark { color: #fff; background: #334155; }
.status-pill--danger { color: #b42318; background: #fee4e2; }
.status-pill--secondary { color: #566273; background: #edf0f3; }

@media (max-width: 1199.98px) {
  .property-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 18px; }
  .summary-item { border-left: 0; }
  .banner-details { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 767.98px) {
  .services-grid { grid-template-columns: 1fr; }
  .property-summary { grid-template-columns: 1fr; }
  .banner-details { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .current-banner__head { align-items: flex-start; flex-direction: column; }
}
</style>

<style>
html[data-bs-theme="dark"] .marketing-materials {
  --pm-text: #e2e9de;
  --pm-muted: #9ba797;
  --pm-surface: #20281e;
  --pm-surface-soft: #252f22;
  --pm-surface-disabled: #1d251b;
  --pm-border: rgba(166, 144, 87, 0.24);
  --pm-border-soft: rgba(166, 144, 87, 0.14);
  --pm-accent: #92bd79;
  --pm-hover: rgba(146, 189, 121, 0.075);
  --pm-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
  color-scheme: dark;
}

html[data-bs-theme="dark"] .marketing-materials .text-muted {
  color: var(--pm-muted) !important;
}

html[data-bs-theme="dark"] .marketing-materials .service-icon--green {
  color: #9ce0bf;
  background: rgba(18, 185, 129, 0.16);
}
html[data-bs-theme="dark"] .marketing-materials .service-icon--blue {
  color: #8ebcff;
  background: rgba(54, 125, 240, 0.17);
}
html[data-bs-theme="dark"] .marketing-materials .service-icon--purple {
  color: #d19aff;
  background: rgba(159, 72, 226, 0.17);
}
html[data-bs-theme="dark"] .marketing-materials .service-icon--solid {
  color: #eff8eb;
  background: #387957;
}

html[data-bs-theme="dark"] .marketing-materials .service-badge--green {
  color: #a8d3b8;
  background: rgba(72, 122, 87, 0.2);
  border-color: rgba(129, 176, 143, 0.25);
}
html[data-bs-theme="dark"] .marketing-materials .service-badge--blue {
  color: #9ebeff;
  background: rgba(57, 94, 151, 0.22);
  border-color: rgba(103, 143, 205, 0.27);
}
html[data-bs-theme="dark"] .marketing-materials .service-badge--purple {
  color: #d4a3f6;
  background: rgba(121, 65, 158, 0.22);
  border-color: rgba(164, 104, 202, 0.26);
}

html[data-bs-theme="dark"] .marketing-materials .service-link--green,
html[data-bs-theme="dark"] .marketing-materials .detail-item--image a {
  color: #8ec9a6;
}
html[data-bs-theme="dark"] .marketing-materials .service-link--blue { color: #83aff2; }
html[data-bs-theme="dark"] .marketing-materials .service-link--purple { color: #c292e6; }

html[data-bs-theme="dark"] .marketing-materials .action-panel .alert-light {
  color: var(--pm-text);
  border-color: var(--pm-border-soft) !important;
  background: var(--pm-surface-soft);
}
html[data-bs-theme="dark"] .marketing-materials .btn-light {
  color: var(--pm-text);
  border-color: var(--pm-border);
  background: #2b3528;
}
html[data-bs-theme="dark"] .marketing-materials .btn-light:hover {
  background: #354131;
}

html[data-bs-theme="dark"] .marketing-materials .execution-sheet-btn {
  color: #e7cb8e;
  border-color: #756239;
  background: #393424;
}

html[data-bs-theme="dark"] .marketing-materials .execution-sheet-btn:hover:not(:disabled) {
  color: #f3dda9;
  border-color: #917846;
  background: #473e28;
}

html[data-bs-theme="dark"] .marketing-materials .status-pill--warning { color: #f0ce77; background: #493b19; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--success { color: #9cdbb9; background: #1f4735; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--primary { color: #a5c5fa; background: #263e63; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--info { color: #94d5e4; background: #204451; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--dark { color: #e1e7ee; background: #394452; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--danger { color: #f2a198; background: #552b27; }
html[data-bs-theme="dark"] .marketing-materials .status-pill--secondary { color: #c2cbc0; background: #384036; }

html[data-bs-theme="dark"] .marketing-materials .detail-item .text-success { color: #8dd4ac !important; }
html[data-bs-theme="dark"] .marketing-materials .detail-item .text-danger { color: #f0a098 !important; }
.marketing-modal-backdrop {position:fixed; inset:0; z-index:1080; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgba(18,31,52,.62)}
.marketing-modal {width:min(100%, 580px); max-height:90vh; overflow:auto; background:#fff; border-radius:15px; padding:24px; box-shadow:0 18px 60px rgba(0,0,0,.2)}
.marketing-modal__head {display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; border-bottom:1px solid #e2e8f0; padding-bottom:1rem; margin-bottom:1.25rem}
.marketing-platform {display:flex; align-items:center; gap:.4rem; border:1px solid #dbe3ee; background:#f8fafc; border-radius:8px; padding:.5rem .8rem; cursor:pointer}
.btn-purple {background:#9333ea; color:#fff; border-color:#9333ea}
.btn-purple:hover {background:#7e22ce; color:#fff}
html[data-bs-theme="dark"] .marketing-modal {background:#1f2937; color:#f8fafc}
html[data-bs-theme="dark"] .marketing-platform {background:#273449; border-color:#475569}
</style>
